import { useTranslation } from "react-i18next";
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationIcon,
} from "@heroicons/react/solid";

import { PlanBadge, Plan } from "pages/Backoffice/TEST/PlanSelection";
import {
  SubscriptionResponse,
  getSubscriptionState,
  useChangeSubscription,
  useOpenBillingPortal,
} from "hooks/useStripe";
import { getRelativeTimeFuture } from "utils/date";
import { useHistory } from "react-router";

interface Props {
  subscription: SubscriptionResponse | null | undefined;
}

export const SubscriptionStatusBanner = ({ subscription }: Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { changeSubscription, isLoading: isReactivating } =
    useChangeSubscription();
  const { openBillingPortal, isLoading: isOpeningPortal } =
    useOpenBillingPortal();

  const uiState = getSubscriptionState(subscription ?? null);

  if (uiState === "free") {
    return null;
  }

  if (uiState === "trial_active") {
    const trialEndLabel = getRelativeTimeFuture(
      (subscription?.trailEnd || 0) * 1000
    );

    return (
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 p-5 shadow-sm">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white" />
          <div className="absolute -right-16 -bottom-16 h-56 w-56 rounded-full bg-white" />
        </div>
        <div className="relative flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20">
              <ClockIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-white">
                  {t("settings.billing.status_trial")}
                </span>
                {subscription?.plan && (
                  <PlanBadge plan={subscription.plan as unknown as Plan} />
                )}
              </div>
              <p className="mt-0.5 text-sm text-white/80">
                {t("settings.billing.trial_label")}: {trialEndLabel}
              </p>
            </div>
          </div>
          <button
            onClick={() => history.push("/settings/billing")}
            className="inline-flex items-center gap-1.5 rounded-md bg-white px-4 py-2 text-sm font-semibold text-blue-600 shadow-sm transition hover:bg-blue-50 whitespace-nowrap"
          >
            {t("settings.billing.action_upgrade")}
          </button>
        </div>
      </div>
    );
  }

  if (uiState === "active") {
    return (
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 p-5 shadow-sm">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white" />
        </div>
        <div className="relative flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20">
            <CheckCircleIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white">
                {t("settings.billing.status_active")}
              </span>
              {subscription?.plan && (
                <PlanBadge plan={subscription.plan as unknown as Plan} />
              )}
            </div>
            <p className="mt-0.5 text-sm text-white/80">
              {t("settings.billing.active_subtitle", {
                frequency: subscription?.frequency,
              })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (uiState === "cancelling") {
    return (
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-amber-400 to-yellow-500 p-5 shadow-sm">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white" />
        </div>
        <div className="relative flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20">
              <ExclamationIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                {subscription?.plan && (
                  <PlanBadge plan={subscription.plan as unknown as Plan} />
                )}
                <span className="inline-flex items-center rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-medium text-white">
                  {t("settings.billing.status_cancelling")}
                </span>
              </div>
              <p className="mt-0.5 text-sm text-white/80">
                {t("settings.billing.cancelling_subtitle")}
              </p>
            </div>
          </div>
          <button
            disabled={isReactivating}
            onClick={() => {
              if (subscription) {
                changeSubscription({
                  plan: subscription.plan,
                  frequency: subscription.frequency,
                });
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-md bg-white px-4 py-2 text-sm font-semibold text-amber-600 shadow-sm transition hover:bg-amber-50 whitespace-nowrap disabled:opacity-50"
          >
            {t("settings.billing.action_reactivate")}
          </button>
        </div>
      </div>
    );
  }

  if (uiState === "payment_pending") {
    return (
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-red-500 to-rose-500 p-5 shadow-sm">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white" />
        </div>
        <div className="relative flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20">
              <ExclamationIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="text-sm font-semibold text-white">
                {t("settings.billing.status_payment_pending")}
              </span>
              <p className="mt-0.5 text-sm text-white/80">
                {t("settings.billing.payment_pending_subtitle")}
              </p>
            </div>
          </div>
          <button
            disabled={isOpeningPortal}
            onClick={() => openBillingPortal()}
            className="inline-flex items-center gap-1.5 rounded-md bg-white px-4 py-2 text-sm font-semibold text-red-600 shadow-sm transition hover:bg-red-50 whitespace-nowrap disabled:opacity-50"
          >
            {t("settings.billing.action_manage")}
          </button>
        </div>
      </div>
    );
  }

  return null;
};
